import { Link } from 'react-router';
import { IconChevronRight, IconClock } from '@tabler/icons-react';
import {
  Badge,
  Metric,
  Panel,
  Progress,
  cn,
  PRESS,
  formatDate,
  formatPercentage,
} from 'escrowbuild-ui';

import { useFixtureVocabulary, usePublishedPublications, useT } from '@/i18n';
import {
  PROJECTS,
  projectTotals,
  participationsForInvestor,
  type Participation,
} from '@/data/domain';

/* =============================================================================
   Investor portal — first screen
   -----------------------------------------------------------------------------
   Everything here is scoped to one person. The portfolio is built from this
   investor's own participations and never from the project list, which is what
   keeps a development they hold nothing in off the page — including the
   published updates of developments they do not participate in, which are
   legitimately public to the platform's other investors and not to this one.
   ============================================================================= */

/**
 * The signed-in investor.
 *
 * `PortalShell` holds the same session — Helena Prado, investor `prado` — and
 * does not export it, so the id is restated rather than read out of the shell's
 * module state.
 */
const INVESTOR_ID = 'prado';

const CARD = 'rounded-xs border border-line bg-surface shadow-hard-sm';
const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';

function shareIn(participations: Participation[], projectId: string): number {
  return participations.find((entry) => entry.projectId === projectId)?.sharePercent ?? 0;
}

/** The `format` union `Metric` accepts. The design system does not re-export its own alias for it. */
type MetricFormat = 'money' | 'percentage' | 'number';

/** Newest first, over a COPY: the caller's list is a fresh one, so the sort
 *  cannot reach a memoised array another reader of it is holding. */
function newestFirst<T extends { publishedAt: string | null }>(rows: T[]): T[] {
  return rows.sort((a, b) => (b.publishedAt ?? '').localeCompare(a.publishedAt ?? ''));
}

function KpiTile({
  label,
  value,
  format,
  hint,
}: {
  label: string;
  value: number;
  format: MetricFormat;
  hint: string;
}) {
  return (
    <li className={cn(CARD, 'p-4')}>
      <Metric label={label} value={value} format={format} hint={hint} />
    </li>
  );
}

export function PortalHomePage() {
  const t = useT();
  const participations = participationsForInvestor(INVESTOR_ID);
  const projectIds = new Set(participations.map((entry) => entry.projectId));
  const projects = PROJECTS.filter((project) => projectIds.has(project.id));

  /* Every released update, in the reader's language, read whole: a development's
     latest one is needed inside a loop below and a hook cannot run there. The
     vocabulary comes with it, for the phase each card names. */
  const releases = usePublishedPublications();
  const vocabulary = useFixtureVocabulary();

  const committed = participations.reduce((sum, entry) => sum + entry.committed, 0);
  const contributed = participations.reduce((sum, entry) => sum + entry.contributed, 0);

  /*
     Two figures are computed here because the domain carries no valuation: her
     ownership percentage is applied to the capital committed to each
     development, and to the part of it not yet spent. A projected sale is not a
     current value, so neither tile is labelled as one — each states its basis.
  */
  const shareValue = projects.reduce(
    (sum, project) => sum + (project.contributed * shareIn(participations, project.id)) / 100,
    0,
  );
  const unspent = projects.reduce(
    (sum, project) => sum + (projectTotals(project.id).available * shareIn(participations, project.id)) / 100,
    0,
  );

  const updates = newestFirst(
    releases.filter((publication) => projectIds.has(publication.projectId)),
  );
  const latest = updates[0];

  return (
    <div className="flex flex-col gap-10">
      <header className="flex flex-col gap-2">
        <p className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
          {t('shell.portal.tagline')}
        </p>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('portal.home.title')}
        </h1>
        <p className="max-w-2xl text-sm text-body">
          {t('portal.home.summary', { count: projects.length })}
          {latest ? t('portal.home.lastUpdated', { date: formatDate(latest.publishedAt) }) : ''}.
        </p>
      </header>

      <section aria-labelledby="portal-totals">
        <h2 id="portal-totals" className="sr-only">
          {t('portal.home.totalsHeading')}
        </h2>
        <ul className="grid list-none gap-3 p-0 sm:grid-cols-2 xl:grid-cols-4">
          <KpiTile
            label={t('portal.home.committed')}
            value={committed}
            format="money"
            hint={t('portal.home.committedHint')}
          />
          <KpiTile
            label={t('portal.home.contributed')}
            value={contributed}
            format="money"
            hint={t('portal.home.contributedHint')}
          />
          <KpiTile
            label={t('portal.home.shareValue')}
            value={shareValue}
            format="money"
            hint={t('portal.home.shareValueHint')}
          />
          <KpiTile
            label={t('portal.home.unspent')}
            value={unspent}
            format="money"
            hint={t('portal.home.unspentHint')}
          />
        </ul>
      </section>

      <section aria-labelledby="portal-developments" className="flex flex-col gap-4">
        <h2
          id="portal-developments"
          className="font-display text-lg font-semibold uppercase tracking-title text-heading"
        >
          {t('portal.home.developmentsHeading')}
        </h2>

        <ul className="grid list-none gap-4 p-0 lg:grid-cols-2">
          {projects.map((project) => {
            const share = shareIn(participations, project.id);
            const lastUpdate = newestFirst(
              releases.filter((publication) => publication.projectId === project.id),
            )[0];

            return (
              <li key={project.id}>
                {/*
                  The card is the link. Nothing interactive sits inside it, so the
                  whole surface is one target and its accessible name is its own
                  contents.
                */}
                <Link
                  to={`/portal/projects/${project.id}`}
                  className={cn(
                    CARD,
                    'flex h-full flex-col gap-4 p-5 no-underline',
                    'transition-colors duration-[var(--dur-fast)] hover:border-primary',
                    PRESS,
                    FOCUS_RING,
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-display text-lg font-semibold uppercase tracking-title text-heading">
                        {project.name}
                      </h3>
                      <p className="mt-1 text-sm text-muted">
                        {project.address} · {project.city}
                      </p>
                    </div>
                    <Badge tone="info" variant="subtle" size="sm">
                      {t('portal.home.share', {
                        share: formatPercentage(share, { precision: 0 }),
                      })}
                    </Badge>
                  </div>

                  <Progress
                    value={project.progress}
                    label={t('portal.home.progress')}
                    showValue
                    size="sm"
                    tone="primary"
                  />

                  <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div className="min-w-0">
                      <dt className="text-2xs uppercase tracking-label text-muted">
                        {t('portal.home.currentPhase')}
                      </dt>
                      <dd className="mt-0.5 truncate text-body">
                        {vocabulary.phase(project.phase)}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-2xs uppercase tracking-label text-muted">
                        {t('portal.home.latestUpdate')}
                      </dt>
                      <dd className="mt-0.5 truncate text-body">
                        {lastUpdate
                          ? formatDate(lastUpdate.publishedAt)
                          : t('portal.home.nonePublished')}
                      </dd>
                    </div>
                  </dl>

                  {lastUpdate ? (
                    <p className="text-sm text-body">{lastUpdate.title}</p>
                  ) : null}

                  <span className="mt-auto inline-flex items-center gap-1.5 pt-1 text-sm font-medium text-primary">
                    {t('portal.home.viewDevelopment')}
                    <IconChevronRight size={14} aria-hidden="true" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      {latest ? (
        <Panel
          title={t('portal.home.latestPanelTitle')}
          description={`${projectName(latest.projectId, t('portal.home.developmentFallback'))} · ${latest.period}`}
          headerActions={
            <Badge tone="success" variant="subtle" size="sm" icon={<IconClock size={12} aria-hidden="true" />}>
              {t('portal.home.released', { date: formatDate(latest.publishedAt) })}
            </Badge>
          }
        >
          <h3 className="font-display text-lg font-semibold uppercase tracking-title text-heading">
            {latest.title}
          </h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-body">{latest.body}</p>

          <dl className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-sm">
            <div>
              <dt className="text-2xs uppercase tracking-label text-muted">
                {t('portal.home.photos')}
              </dt>
              <dd className="mt-0.5 text-body">{latest.photos}</dd>
            </div>
            <div>
              <dt className="text-2xs uppercase tracking-label text-muted">
                {t('portal.home.documents')}
              </dt>
              <dd className="mt-0.5 text-body">{latest.documents}</dd>
            </div>
            <div>
              <dt className="text-2xs uppercase tracking-label text-muted">
                {t('portal.home.approvedBy')}
              </dt>
              <dd className="mt-0.5 text-body">{latest.approvedBy}</dd>
            </div>
            <div>
              <dt className="text-2xs uppercase tracking-label text-muted">
                {t('portal.home.releasedLabel')}
              </dt>
              <dd className="mt-0.5 text-body">{formatDate(latest.publishedAt)}</dd>
            </div>
          </dl>

          <Link
            to={`/portal/updates/${latest.id}`}
            className={cn(
              'mt-5 inline-flex items-center gap-1.5 rounded-xs text-sm font-medium text-primary no-underline hover:text-primary-hover',
              FOCUS_RING,
            )}
          >
            {t('portal.home.readUpdate')}
            <IconChevronRight size={14} aria-hidden="true" />
          </Link>
        </Panel>
      ) : null}
    </div>
  );
}

/** The development's name for a publication's header, without leaking the full project record. */
function projectName(projectId: string, fallback: string): string {
  return PROJECTS.find((project) => project.id === projectId)?.name ?? fallback;
}
